import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Platform,
  StyleSheet,
} from 'react-native';
import RecordDatePicker from './RecordDatePicker';

function normalizeDraftText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function RecordCellDropdown({
  options,
  selectedLabel,
  inputType,
  onChange,
  textColor,
  borderColor,
  surface,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const triggerRef = useRef(null);

  const closeDropdown = useCallback(() => {
    setDropdownOpen(false);
    setMenuRect(null);
  }, []);

  const openDropdown = useCallback(() => {
    if (Platform.OS === 'web' && triggerRef.current?.measureInWindow) {
      triggerRef.current.measureInWindow((x, y, width, height) => {
        setMenuRect({
          top: y + height + 2,
          left: x,
          width: Math.max(width, 120),
        });
        setDropdownOpen(true);
      });
      return;
    }
    setDropdownOpen(true);
  }, []);

  const toggleDropdown = useCallback(() => {
    if (dropdownOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [dropdownOpen, closeDropdown, openDropdown]);

  return (
    <View style={[styles.wrap, dropdownOpen && styles.wrapOpen]}>
      <View ref={triggerRef} collapsable={false} style={styles.triggerShell}>
        <TouchableOpacity
          style={[styles.dropdownBtn, { borderColor, backgroundColor: surface }]}
          onPress={toggleDropdown}
          {...(Platform.OS === 'web'
            ? { dataSet: { recordCellEditor: 'trigger' } }
            : {})}
        >
          <Text style={[styles.dropdownBtnText, { color: textColor }]} numberOfLines={1}>
            {selectedLabel || '—'}
          </Text>
          <Text style={[styles.chevron, { color: textColor }]}>
            {dropdownOpen ? '▲' : '▼'}
          </Text>
        </TouchableOpacity>
      </View>
      {dropdownOpen ? (
        <>
          {Platform.OS === 'web' ? (
            <TouchableOpacity
              style={styles.overlay}
              activeOpacity={1}
              onPress={closeDropdown}
            />
          ) : null}
          <View
            style={[
              styles.menu,
              { borderColor, backgroundColor: surface },
              Platform.OS === 'web' && menuRect
                ? {
                    position: 'fixed',
                    top: menuRect.top,
                    left: menuRect.left,
                    width: menuRect.width,
                    right: undefined,
                  }
                : null,
            ]}
            {...(Platform.OS === 'web'
              ? { dataSet: { recordCellEditor: 'menu' } }
              : {})}
          >
            <ScrollView style={styles.menuScroll} nestedScrollEnabled>
              {options.map((opt) => (
                <TouchableOpacity
                  key={String(opt)}
                  style={styles.menuItem}
                  onPress={() => {
                    if (inputType === 'boolean') {
                      onChange(opt === 'Yes');
                    } else {
                      onChange(opt);
                    }
                    closeDropdown();
                  }}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { recordCellEditor: 'menu-item' } }
                    : {})}
                >
                  <Text style={[styles.menuItemText, { color: textColor }]}>
                    {opt}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </>
      ) : null}
    </View>
  );
}

export default function RecordCellEditor({
  value,
  fieldMeta,
  theme,
  onChange,
  isUSUser = false,
}) {
  const inputType = fieldMeta?.input_type || 'text';
  const picklistValues = fieldMeta?.picklist_values || null;
  const displayValue = normalizeDraftText(value);

  const textColor = theme.isDarkMode ? '#FFFFFF' : '#111111';
  const borderColor = theme.isDarkMode ? '#555555' : '#CCCCCC';
  const surface = theme.isDarkMode ? '#1a1a1a' : '#FFFFFF';

  if (inputType === 'date' || inputType === 'datetime') {
    return (
      <RecordDatePicker
        value={value}
        onChange={onChange}
        theme={theme}
        isUSUser={isUSUser}
        preserveTime={inputType === 'datetime'}
        allowClear={fieldMeta?.is_nullable !== false}
      />
    );
  }

  if (inputType === 'boolean' || (picklistValues && picklistValues.length > 0)) {
    const options =
      inputType === 'boolean' ? ['Yes', 'No'] : picklistValues;
    const selectedLabel =
      inputType === 'boolean'
        ? value === true || value === 'true' || value === 'Yes'
          ? 'Yes'
          : value === false || value === 'false' || value === 'No'
            ? 'No'
            : displayValue
        : displayValue;

    return (
      <RecordCellDropdown
        options={options}
        selectedLabel={selectedLabel}
        inputType={inputType}
        onChange={onChange}
        textColor={textColor}
        borderColor={borderColor}
        surface={surface}
      />
    );
  }

  if (inputType === 'json_array') {
    return (
      <TextInput
        style={[
          styles.input,
          styles.jsonArrayInput,
          {
            color: textColor,
            borderColor,
            backgroundColor: surface,
          },
        ]}
        value={displayValue}
        onChangeText={onChange}
        placeholder='["https://youtube.com/..."]'
        placeholderTextColor={theme.isDarkMode ? '#777777' : '#999999'}
        multiline
        {...(Platform.OS === 'web'
          ? { dataSet: { recordCellEditor: 'json-array' } }
          : {})}
      />
    );
  }

  return (
    <TextInput
      style={[
        styles.input,
        {
          color: textColor,
          borderColor,
          backgroundColor: surface,
        },
      ]}
      value={displayValue}
      onChangeText={onChange}
      placeholder="—"
      placeholderTextColor={theme.isDarkMode ? '#777777' : '#999999'}
      keyboardType={inputType === 'number' ? 'numeric' : 'default'}
      {...(Platform.OS === 'web'
        ? { dataSet: { recordCellEditor: 'input' } }
        : {})}
    />
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'relative',
    width: '100%',
    zIndex: 1,
    backgroundColor: 'transparent',
  },
  wrapOpen: {
    zIndex: 1000,
  },
  triggerShell: {
    width: '100%',
    backgroundColor: 'transparent',
  },
  input: {
    width: '100%',
    minHeight: 32,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 4,
    fontSize: 13,
  },
  jsonArrayInput: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  dropdownBtn: {
    minHeight: 32,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  dropdownBtnText: {
    flex: 1,
    fontSize: 13,
    backgroundColor: 'transparent',
  },
  chevron: {
    fontSize: 10,
    marginLeft: 4,
    backgroundColor: 'transparent',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10040,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 2,
    borderWidth: 1,
    borderRadius: 4,
    maxHeight: 160,
    zIndex: 10050,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 12px rgba(0,0,0,0.35)' }
      : {
          elevation: 8,
          shadowColor: '#000',
          shadowOpacity: 0.25,
          shadowRadius: 6,
          shadowOffset: { width: 0, height: 2 },
        }),
  },
  menuScroll: {
    maxHeight: 160,
  },
  menuItem: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: 'transparent',
  },
  menuItemText: {
    fontSize: 13,
    backgroundColor: 'transparent',
  },
});
